<template>
  <div>
    <v-card-text class="mt-4 pa-0">
      <p v-if="tempUser.hasSecret">
        {{ $t("enterVerificationCodeIntro") }}
      </p>
      <i18n v-else path="addAppToAuthenticatorInstruction" tag="p">
        <a
          href="https://helpguide.smarthotel.nl/en/how-do-i-set-up-2-factor-authentication"
          target="_blank"
          >{{ $t("here") }}</a
        >
      </i18n>
      <div
        class="d-flex flex-column"
        :class="!tempUser.hasSecret ? 'align-center' : ''"
      >
        <template v-if="secret">
          <qr-code :qr-code-url="qrCodeUrl"></qr-code>
          <p class="mb-2">
            <span> {{ $t("2faIssuer") }} SmartCONNECT</span>
            <v-btn v-clipboard:copy="'SmartCONNECT'" small icon>
              <v-icon size="20" color="primary">$copy</v-icon>
            </v-btn>
          </p>
          <p class="mb-8">
            <span> {{ $t("2faSecret") }} {{ secret }}</span>
            <v-btn v-clipboard:copy="secret" small icon>
              <v-icon size="20" color="primary">$copy</v-icon>
            </v-btn>
          </p>
        </template>

        <v-form ref="qrCodeForm" @submit.prevent>
          <v-text-field
            ref="verificationCode"
            v-model.trim="verificationCode"
            class="verification-code-input"
            :rules="validationRules('code')"
            outlined
            maxlength="6"
            autocomplete="off"
            type="text"
            :label="$t('verificationCode')"
            validate-on-blur
            @keydown.space.prevent
            @keyup.enter="verifyCode"
          >
            <template v-if="tempUser.hasSecret" #append-outer>
              <text-popover
                :text="$t('verrificationCodePopoverMessage')"
              ></text-popover>
            </template>
            <template #message="{ message }">
              {{ $t(message) }}
            </template>
          </v-text-field>
        </v-form>
      </div>
    </v-card-text>
    <v-card-actions class="pa-0 my-3">
      <v-spacer></v-spacer>
      <v-btn
        id="send-verification-code-button"
        class="button"
        color="primary"
        @click="verifyCode"
        >{{ $t("send") }}
      </v-btn>
    </v-card-actions>
  </div>
</template>
<script>
import QrCode from "./QrCode";
import TextPopover from "@/components/globals/TextPopover";
import { axiosPost, axiosGet } from "@/api/requests";
import snackBarMessages from "@/mixins/snackBarMessages";

export default {
  name: "TwoFactorAuthentication",
  components: {
    QrCode,
    TextPopover,
  },
  mixins: [snackBarMessages],
  props: {
    tempUser: {
      type: Object,
      required: false,
      default: () => {
        return null;
      },
    },
  },
  data() {
    return {
      secret: "",
      verificationCode: "",
    };
  },
  computed: {
    qrCodeUrl() {
      return (
        "otpauth://totp/Payment%20Studio?secret=" +
        this.secret +
        "&issuer=SmartCONNECT&algorithm=SHA1&digits=6&period=30"
      );
    },
  },
  watch: {
    tempUser: {
      immediate: true,
      handler(user) {
        if (user && !user.hasSecret) {
          this.setSecret(user);
        }
      },
    },
  },
  methods: {
    setSecret(user) {
      const url = process.env.VUE_APP_IDENTITY_URL + "/admin/";
      const data = {
        command: "SetSecret",
        data: {
          ID: user.accountID,
        },
      };
      axiosPost(url, data, user.authToken)
        .then((response) => {
          this.handleSetSecretResponse(response);
        })
        .catch((error) => {
          this.snackbarError(this.$t("undefinedError"));
        })
        .finally(() => {});
    },
    handleSetSecretResponse(response) {
      if (response.success && response.secret !== "") {
        this.secret = response.secret;
      } else {
        this.snackbarError(response.errorMsg);
      }
    },
    verifyCode() {
      if (this.$refs.qrCodeForm.validate()) {
        this.sendVerifyCodeRequest();
      } else {
        this.snackbarInvalidForm();
      }
    },
    sendVerifyCodeRequest() {
      const isprod = process.env.VUE_APP_MODE === "PROD";
      const url =
        process.env.VUE_APP_IDENTITY_URL +
        "/2fa/" +
        "?id=" +
        this.tempUser.accountID +
        "&code=" +
        this.verificationCode +
        "&isprod=" +
        isprod;

      axiosGet(url, this.tempUser.authToken)
        .then((response) => {
          this.handleSendVerifyCodeResponse(response);
        })
        .catch((error) => {
          this.snackbarError(this.$t("codeVerificationFailed"));
        })
        .finally(() => {});
    },
    handleSendVerifyCodeResponse(response) {
      if (response.isAuthenticated && response.hasSecret) {
        response.loggedIn2fa = true;
        this.$emit("handle-login-success", {
          user: response,
        });
      } else {
        this.snackbarError(this.$t("codeVerificationFailed"));
      }
    },
    validationRules(fieldname) {
      switch (fieldname) {
        case "code":
          return [
            (v) => !!v || "invalidVerificationCodeError",
            (v) => /^[0-9]*$/.test(v) || "invalidVerificationCodeError",
            (v) => v.length === 6 || "invalidVerificationCodeError",
          ];
      }
    },
  },
};
</script>
